import {purposePolicy, RevaValuationPurpose, VALUATION_PURPOSES} from "@reva/shared/valuation-purpose";

import {
    checkPermissionRule,
    PERMISSION_RULE_LOCATION_POIS,
    PERMISSION_RULE_PURPOSE_RESEARCH,
    PERMISSION_RULE_PURPOSE_VALUATION,
    type PermissionRule,
} from "./permissions";
import type {RevaPermission} from "./revaPermission";

/** Recht, das zum Anlegen einer REVA-ID des Zwecks und zum Zweckwechsel darauf berechtigt (#19481, bewusst ohne admins). */
export const PURPOSE_PERMISSION_RULES: Record<RevaValuationPurpose, PermissionRule> = {
    RESEARCH: PERMISSION_RULE_PURPOSE_RESEARCH,
    VALUATION: PERMISSION_RULE_PURPOSE_VALUATION,
};

/**
 * Die Zwecke, mit denen der User neue REVA-IDs anlegen darf, in Anzeigereihenfolge.
 * @param permissions die Rechte des Users
 */
export function creatablePurposes(permissions: RevaPermission[]): RevaValuationPurpose[] {
    return VALUATION_PURPOSES.filter((purpose) => checkPermissionRule(permissions, PURPOSE_PERMISSION_RULES[purpose]));
}

/**
 * #19481: Research-only-User — darf ausschließlich Research-Abfragen anlegen (reva-purpose-research, aber nicht
 * reva-purpose-valuation). Er sieht auf der Startseite nur den Reiter „Research-Abfragen", alle
 * anderen Startseiten-Routen leiten dorthin um. Einzige Stelle dieser Regel für Router und Startseite.
 * @param permissions die Rechte des Users
 */
export function isResearchOnly(permissions: RevaPermission[]): boolean {
    const purposes = creatablePurposes(permissions);
    return purposes.length === 1 && purposes[0] === RevaValuationPurpose.RESEARCH;
}

/**
 * Wie eine neue REVA-ID ihren Zweck bekommt: `purpose` = direkt so anlegen, `ask` = Auswahldialog, `forbidden` =
 * der User darf gar nicht anlegen (bewusst kein stiller Rückfall auf Bewertung).
 */
export type CreationPurposeDecision =
    | {kind: "purpose"; purpose: RevaValuationPurpose}
    | {kind: "ask"; options: RevaValuationPurpose[]}
    | {kind: "forbidden"};

/**
 * Entscheidet über den Zweck einer neuen REVA-ID (#19481): ein angefragter Zweck (Einstiegs-URL, schon im Dialog
 * gewählt) gilt, wenn der User ihn anlegen darf; sonst gilt die übliche Regel — genau ein erlaubter Zweck wird ohne
 * Rückfrage genommen, bei mehreren fragt der Dialog, ohne jedes Recht wird nicht angelegt.
 * @param permissions die Rechte des Users
 * @param requested angefragter Zweck oder null
 */
export function resolveCreationPurpose(
    permissions: RevaPermission[],
    requested: RevaValuationPurpose | null | undefined,
): CreationPurposeDecision {
    const allowed = creatablePurposes(permissions);
    if (requested && allowed.includes(requested)) return {kind: "purpose", purpose: requested};
    if (allowed.length === 0) return {kind: "forbidden"};
    if (allowed.length === 1) return {kind: "purpose", purpose: allowed[0]};
    return {kind: "ask", options: allowed};
}

/**
 * Zweck, den eine Einstiegs-URL der Startseite vorgibt (MK #19481-17): `/start/research` (zugleich der
 * Research-Reiter) legt Research an, `/start/valuation` Bewertungen; alle anderen Pfade geben nichts vor.
 * @param pathname aktueller Pfad
 */
export function entryPurposeForPath(pathname: string): RevaValuationPurpose | null {
    const segments = pathname.split("/").filter(Boolean);
    if (segments[0] !== "start") return null;
    if (segments[1] === "research") return RevaValuationPurpose.RESEARCH;
    if (segments[1] === "valuation") return RevaValuationPurpose.VALUATION;
    return null;
}

/**
 * Seite, auf der eine von der Startseite aus geöffnete bzw. neu angelegte REVA-ID startet (#19481): laut
 * {@link purposePolicy} die Lage (POIs, Research) oder die Objekt-Übersicht (Bewertung). Ohne POI-Recht bzw. ohne
 * Recht für die Objektseite fällt sie wie bisher auf Objekt-Übersicht bzw. Adressseite zurück. Deep-Links auf eine
 * andere Seite laufen nicht hierüber.
 * @param purpose Zweck der geladenen REVA-ID (fehlend = Bewertung)
 * @param permissions die Rechte des Users
 * @param objectPageAllowed ob der User die Objektseite sehen darf
 */
export function valuationEntryRoute(
    purpose: string | null | undefined,
    permissions: RevaPermission[],
    objectPageAllowed: boolean,
): string {
    if (
        purposePolicy(purpose).entryPage === "LOCATION" &&
        checkPermissionRule(permissions, PERMISSION_RULE_LOCATION_POIS)
    ) {
        return "/location/points-of-interest";
    }
    return objectPageAllowed ? "/object/overview" : "/address";
}
